import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, ShieldCheck, ArrowRight, Eye, EyeOff, RefreshCw, X, Globe, ChevronDown } from 'lucide-react';
import { db } from '../firebase'; 
import { ref, set, get } from "firebase/database"; 
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import './Register.css';

const Register = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [captchaText, setCaptchaText] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");

  const languages = [
    { code: 'en', flag: '🇺🇸', label: 'EN' }, 
    { code: 'es', flag: '🇪🇸', label: 'ES' }, 
    { code: 'fa', flag: '🇮🇷', label: 'FA' }, 
    { code: 'ar', flag: '🇸🇦', label: 'AR' }, 
    { code: 'tr', flag: '🇹🇷', label: 'TR' }
  ];

  const generateCaptcha = () => {
    const numbers = "123456789"; 
    let result = "";
    for (let i = 0; i < 4; i++) {
      result += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }
    setCaptchaText(result);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      Swal.fire({ icon: 'warning', title: t('weak_pass'), text: t('pass_short') });
      return;
    }
    if (captchaInput !== captchaText) {
      Swal.fire({ icon: 'error', title: t('invalid_captcha'), text: t('wrong_code') });
      generateCaptcha();
      return;
    }

    try {
      Swal.fire({ title: t('processing'), didOpen: () => { Swal.showLoading() }, allowOutsideClick: false });
      const userRef = ref(db, 'users/' + username);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        Swal.fire({ icon: 'error', title: t('oops'), text: t('user_exists') });
        return;
      }

      await set(userRef, { username, email, password, balance: 0, role: "user", createdAt: new Date().toISOString() });
      Swal.fire({ icon: 'success', title: t('reg_success'), text: t('redirecting'), timer: 2500, showConfirmButton: false });
      setTimeout(() => { navigate('/login'); }, 2500);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    }
  };

  return (
    <div className="reg-container">
      <div className="reg-card" style={{ position: 'relative' }}>
        {/* Language Selector */}
        <div className="lang-container" style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}>
            <button className="lang-btn" onClick={() => setShowLangMenu(!showLangMenu)} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', background: 'transparent', border: '1px solid #ddd', padding: '5px 10px', borderRadius: '5px' }}>
                <Globe size={16} /> <span>{i18n.language.toUpperCase()}</span> <ChevronDown size={14} />
            </button>
            {showLangMenu && (
                <div className="lang-dropdown" style={{ position: 'absolute', top: '100%', right: '0', background: 'white', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', zIndex: 20 }}>
                    {languages.map((l) => (
                        <div key={l.code} onClick={() => { i18n.changeLanguage(l.code); setShowLangMenu(false); }} style={{ cursor: 'pointer', padding: '5px', display: 'flex', gap: '5px' }}>
                            {l.flag} {l.label}
                        </div>
                    ))}
                </div>
            )}
        </div>

        <div className="reg-header">
          <h1>{t('create_account')}</h1>
          <p>{t('start_journey')}</p>
        </div>

        <form className="reg-form" onSubmit={handleRegister}>
          <div className="input-group">
            <label>{t('username')}</label>
            <div className="input-wrapper">
              <User size={18} className="input-icon" />
              <input type="text" placeholder={t('placeholder_user')} value={username} onChange={(e) => setUsername(e.target.value)} required />
            </div>
          </div>

          <div className="input-group">
            <label>{t('password')}</label>
            <div className="input-wrapper">
              <Lock size={18} className="input-icon" />
              <input type={showPassword ? "text" : "password"} placeholder={t('placeholder_pass')} value={password} onChange={(e) => setPassword(e.target.value)} required />
              <button type="button" className="eye-toggle" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>{t('email')}</label>
            <div className="input-wrapper">
              <Mail size={18} className="input-icon" />
              <input type="email" placeholder={t('placeholder_email')} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
          </div>

          <div className="input-group">
            <label>{t('verification')}</label>
            <div className="captcha-container">
              <div className="input-wrapper captcha-input">
                <ShieldCheck size={18} className="input-icon" />
                <input type="text" placeholder={t('enter_code')} maxLength="4" value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value)} required />
              </div>
              <div className="captcha-visual-modern" onClick={generateCaptcha}>
                <svg width="100" height="45" viewBox="0 0 100 45">
                  <text x="50%" y="65%" textAnchor="middle" className="captcha-text-main">{captchaText}</text>
                </svg>
                <RefreshCw size={14} className="captcha-refresh" />
              </div>
            </div>
          </div>

          <div className="terms-check">
            <input type="checkbox" id="terms" required />
            <label htmlFor="terms">
              {t('i_agree')} <span onClick={() => setShowTerms(true)}>{t('terms_cond')}</span>
            </label>
          </div>

          <button type="submit" className="reg-btn">
            {t('register_now')} <ArrowRight size={18} />
          </button>
        </form>

        <div className="reg-footer">
          <p>{t('already_have_acc')} <a href="/login">{t('login_here')}</a></p>
        </div>
      </div>

      {showTerms && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('terms_cond')}</h3>
              <X className="close-icon" onClick={() => setShowTerms(false)} />
            </div>
            <div className="modal-body">
              <p>{t('term1')}</p>
              <p>{t('term2')}</p>
              <p>{t('term3')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;